import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {POLICY_TEMPLATE_FRAGMENT} from 'auth/PolicyConstants';
import * as PolicyDAO from 'auth/PolicyDAO';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {SchemaPolicyPageQuery, SchemaPolicyPageQueryVariables} from './__generated/SchemaPolicyPageQuery';
import PolicyPage from './PolicyPage';

type SchemaPolicyOrInaccessibleInfo = SchemaPolicyPageQuery['schemaPolicy'];

type SchemaPolicy = ExtractByTypename<SchemaPolicyOrInaccessibleInfo, 'SchemaPolicyNode'>;

export const SCHEMA_POLICY_PAGE_QUERY = gql`
  query SchemaPolicyPageQuery($id: StrictString!) @skipAudit @engComponent(name: PERMISSIONS) {
    schemaPolicy(id: $id) {
      ... on SchemaPolicyNode {
        id
        name
        permissions {
          readable
          writable
        }
        policyStatements {
          itemType
          action
          condition
        }
      }
      ...InaccessibleInfoGraphQL
    }
    policyTemplate(policyType: SCHEMA) {
      ...PolicyTemplate
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${POLICY_TEMPLATE_FRAGMENT}
`;

interface Props {
  schemaPolicyId: string;
}

function isAccessible(schemaPolicy: SchemaPolicyOrInaccessibleInfo): schemaPolicy is SchemaPolicy {
  return !isInaccessible(schemaPolicy);
}

export default function SchemaPolicyPage(props: Props): JSX.Element | null {
  const result = useBenchlingQuery<SchemaPolicyPageQuery, SchemaPolicyPageQueryVariables>(
    SCHEMA_POLICY_PAGE_QUERY,
    {variables: {id: props.schemaPolicyId}}
  );

  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load schema policy; try reloading the page.');
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    const schemaPolicy = result.data.schemaPolicy;
    assert(isAccessible(schemaPolicy), 'Expected policy currently being viewed to be readable');
    return (
      <PolicyPage
        policy={schemaPolicy}
        initialSpec={{
          name: schemaPolicy.name,
          statements: schemaPolicy.policyStatements,
        }}
        listingsTitle="Schema Access Policies"
        policiesURL={PolicyDAO.getSchemaPoliciesURL()}
        policyTemplate={result.data.policyTemplate}
        // Schema policies do not support mutations from end users.
        updatePolicy={(PolicySpec) => Promise.reject(new Error('Not implemented'))}
      />
    );
  }
}
